import { QueryClient, VueQueryPlugin, dehydrate, hydrate } from '@tanstack/vue-query'
import { defineNuxtPlugin, useState } from 'nuxt/app'
import type { DehydratedState, VueQueryPluginOptions } from '@tanstack/vue-query'

/**
 * Vue Query plugin - shared across all apps
 * Sets up TanStack Query with SSR hydration support
 */
export default defineNuxtPlugin(nuxt => {
  const vueQueryState = useState<DehydratedState | null>('vue-query')

  // Create a new QueryClient instance for each request
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // SSR retries run after the request ends (no Nuxt/Pinia context → lost auth).
        // Client keeps one retry for transient network failures.
        retry: import.meta.server ? false : 1,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      },
    },
  })

  const options: VueQueryPluginOptions = { queryClient }

  nuxt.vueApp.use(VueQueryPlugin, options)

  // SSR hydration
  if (import.meta.server) {
    nuxt.hooks.hook('app:rendered', () => {
      vueQueryState.value = dehydrate(queryClient)
      // Stop in-flight SSR fetches so they do not continue outside the request.
      void queryClient.cancelQueries()
    })
  }

  if (import.meta.client) {
    // Hydrate immediately on client to ensure queries don't refetch
    if (vueQueryState.value) {
      hydrate(queryClient, vueQueryState.value)
    }
  }

  // Expose queryClient to nuxtApp for middleware access
  return {
    provide: {
      queryClient,
    },
  }
})
